import React, { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'react-toastify'
import adminService from '../../services/adminService'
import Skeleton from 'react-loading-skeleton'

// Defaults applied when a key isn't yet present in system_settings.
const DEFAULTS = {
  maintenanceMode: false,
  registrationEnabled: true,
  emailNotifications: true,
  maxUsersPerPlan: { free: 1000, pro: 10000, business: -1 }
}

// Module-level cache. Survives component remounts so a transient parent
// re-render (cloud-sync tick, auth refresh, etc.) cannot wipe in-progress
// edits. Cleared on Save and on explicit Reset.
const sessionCache = {
  settings: null,
  dirty: false,
  updatedAt: null
}

const AdminSettings = () => {
  const [settings, setSettings] = useState(() => sessionCache.settings || DEFAULTS)
  const [loading, setLoading] = useState(!sessionCache.settings)
  const [saving, setSaving] = useState(false)
  const [updatedAt, setUpdatedAt] = useState(sessionCache.updatedAt)
  const [dirty, setDirty] = useState(sessionCache.dirty)
  const dirtyRef = useRef(sessionCache.dirty)

  // Keep ref + module cache in sync with state so async load() callbacks see
  // the latest "did the user touch the form" value without needing to be
  // recreated.
  useEffect(() => {
    dirtyRef.current = dirty
    sessionCache.dirty = dirty
  }, [dirty])

  useEffect(() => {
    if (settings) sessionCache.settings = settings
  }, [settings])

  const applyServerValues = useCallback((values, latest) => {
    setSettings({
      maintenanceMode: values.maintenanceMode ?? DEFAULTS.maintenanceMode,
      registrationEnabled: values.registrationEnabled ?? DEFAULTS.registrationEnabled,
      emailNotifications: values.emailNotifications ?? DEFAULTS.emailNotifications,
      maxUsersPerPlan: values.maxUsersPerPlan ?? DEFAULTS.maxUsersPerPlan
    })
    setUpdatedAt(latest)
    sessionCache.updatedAt = latest
  }, [])

  const load = useCallback(async ({ force = false } = {}) => {
    // If the user has unsaved edits, keep their work — skip the refetch.
    if (!force && dirtyRef.current) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const { values, rows } = await adminService.getSystemSettings()
      // Re-check after the await — user may have started editing in the gap.
      if (!force && dirtyRef.current) return
      const latest = rows.reduce((acc, r) => {
        if (!acc || new Date(r.updated_at) > new Date(acc.updated_at)) return r
        return acc
      }, null)
      applyServerValues(values, latest)
    } catch (err) {
      toast.error('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [applyServerValues])

  // Initial fetch on mount. The dirty-guard inside load() means a remount
  // mid-edit will not clobber the user's work.
  useEffect(() => { load() }, [load])

  const updateSettings = useCallback((updater) => {
    setSettings(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      sessionCache.settings = next
      return next
    })
    if (!dirtyRef.current) {
      dirtyRef.current = true
      setDirty(true)
    }
  }, [])

  const handleSave = async () => {
    try {
      setSaving(true)
      await adminService.saveSystemSettings(settings)
      // Clear dirty BEFORE the reload so load() will accept the fresh values.
      dirtyRef.current = false
      sessionCache.dirty = false
      setDirty(false)
      toast.success('Settings saved')
      await load({ force: true })
    } catch (err) {
      toast.error(err.message || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    dirtyRef.current = false
    sessionCache.dirty = false
    setDirty(false)
    load({ force: true })
  }

  const Toggle = ({ checked, onChange }) => (
    <button
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  )

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Skeleton height={32} width={200} className="mb-6" />
        <Skeleton count={6} height={60} className="mb-3" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
          <p className="text-gray-600">Configure application settings and preferences</p>
        </div>
        {dirty && (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">
            Unsaved changes
          </span>
        )}
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application toggles</h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Maintenance Mode</label>
                <p className="text-sm text-gray-500">Temporarily disable access to the application</p>
              </div>
              <Toggle
                checked={settings.maintenanceMode}
                onChange={() => updateSettings(prev => ({ ...prev, maintenanceMode: !prev.maintenanceMode }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">User Registration</label>
                <p className="text-sm text-gray-500">Allow new users to register</p>
              </div>
              <Toggle
                checked={settings.registrationEnabled}
                onChange={() => updateSettings(prev => ({ ...prev, registrationEnabled: !prev.registrationEnabled }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                <p className="text-sm text-gray-500">Send admin email notifications for important events</p>
              </div>
              <Toggle
                checked={settings.emailNotifications}
                onChange={() => updateSettings(prev => ({ ...prev, emailNotifications: !prev.emailNotifications }))}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Per-plan user caps</h3>
          <p className="text-xs text-gray-500 mb-3">
            Default usage limit applied per user when their subscription tier is set on the User
            Management page. Use -1 (or leave blank) for unlimited. Existing users keep their
            current limits until you save them with a new tier.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(settings.maxUsersPerPlan).map(([planKey, value]) => (
              <div key={planKey}>
                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                  {planKey}
                </label>
                <input
                  type="number"
                  value={value === -1 ? '' : value}
                  placeholder="Unlimited (-1)"
                  onChange={(e) => updateSettings(prev => ({
                    ...prev,
                    maxUsersPerPlan: {
                      ...prev.maxUsersPerPlan,
                      [planKey]: e.target.value === '' ? -1 : parseInt(e.target.value, 10)
                    }
                  }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            ))}
          </div>
        </div>

        {updatedAt && (
          <div className="text-xs text-gray-500">
            Last changed {new Date(updatedAt.updated_at).toLocaleString()}
            {updatedAt.updated_by ? ` by ${updatedAt.updated_by}` : ''}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdminSettings
